import React, { useCallback, useMemo, useState } from "react";
import styles from "./Archived.module.scss";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../components/Table/Table";
import Button from "../../components/Button/Button";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/utils";
import Swal from "sweetalert2";
import { getOrganizationData } from "../../actions/organization";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import { showOverlay, hideOverlay } from "../../redux/overlayLoaderStore";
import {
  resetSelection,
  clearResetSelection,
} from "../../redux/rowSelectionStore";
import { formatDate } from "../../utils/helpers";
import _ from "lodash";
import classNames from "classnames";
import Input from "../../components/Input/Input";
import { ReactComponent as SearchIcon } from "../../icons/search.svg";
import { useTranslation } from "react-i18next";
import ArchiveIllustration from "../../icons/archive-illustration.svg";
import {
  AlertCircle,
  Archive,
  FileSearch,
  RotateCcw,
  TrendingUp,
} from "lucide-react";

const Archived = () => {
  const { orgId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const sessionToken = localStorage.getItem("session_token");

  const [activeTab, setActiveTab] = useState("audits");

  const [selectedAudits, setSelectedAudits] = useState([]);
  const [selectedFindings, setSelectedFindings] = useState([]);
  const [selectedImprovements, setSelectedImprovements] = useState([]);
  const [searchQueries, setSearchQueries] = useState({
    audits: "",
    findings: "",
    improvements: "",
  });

  const { auditsTasksData, findingsData, improvementsData } = useSelector(
    (state) => state.organization,
  );
  const archivedAudits = useMemo(
    () => Object.values(auditsTasksData || {}).filter((a) => a.archived),
    [auditsTasksData],
  );
  const archivedFindings = useMemo(
    () => Object.values(findingsData || {}).filter((f) => f.archived),
    [findingsData],
  );
  const archivedImprovements = useMemo(
    () => Object.values(improvementsData || {}).filter((i) => i.archived),
    [improvementsData],
  );

  const totalArchived =
    archivedAudits.length + archivedFindings.length + archivedImprovements.length;

  const resetRowSelection = useSelector(
    (state) => state.rowSelection.resetSelection,
  );

  const resetAllSelectionState = () => {
    setSelectedAudits([]);
    setSelectedFindings([]);
    setSelectedImprovements([]);
    dispatch(resetSelection());
    window.setTimeout(() => dispatch(clearResetSelection()), 100);
  };

  const handleUnarchive = async (items, type) => {
    if (!items.length) return;

    const payload = items.map((item) => ({ id: item.id, archived: false }));
    const endpointMap = {
      audits: "/nocodb/audit_tasks",
      findings: "/nocodb/findings",
      improvements: "/nocodb/improvements",
    };

    const loadFlags = {
      audits: [false, false, true], // loadAuditTasks
      findings: [true, false, false], // loadFindings
      improvements: [false, true, false], // loadImprovements
    };

    dispatch(showOverlay());

    try {
      await axiosInstance.patch(
        `${endpointMap[type]}?org_id=${orgId}`,
        payload,
        {
          headers: { "session-token": sessionToken },
        },
      );

      const [loadFindings, loadImprovements, loadAuditTasks] =
        loadFlags[type] || [];

      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          loadFindings,
          loadImprovements,
          loadAuditTasks,
          false,
          false,
        ),
      );

      const unarchivedKeys = items.map((item) => item.key).join(", ");

      Swal.fire({
        toast: true,
        icon: "success",
        title: t("archived.swal.successTitle"),
        text: t("archived.swal.successText", { keys: unarchivedKeys }),
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } catch (err) {
      Swal.fire({
        toast: true,
        icon: "error",
        title: t("archived.swal.errorTitle"),
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      dispatch(hideOverlay());
      resetAllSelectionState();
    }
  };

  const truncateText = (text, maxLength) => {
    if (text?.length <= maxLength) {
      return text;
    }
    return (
      <>
        {text?.substring(0, maxLength)}
        <span className={styles.readMore}>{t("archived.readMore")}</span>
      </>
    );
  };

  const findingByKey = useMemo(() => {
    const entries = Object.values(findingsData || {}).map((finding) => [
      finding.key,
      finding,
    ]);
    return new Map(entries);
  }, [findingsData]);

  const normalize = useCallback((value) => (value ?? "").toString().toLowerCase(), []);

  const getImprovementRisks = useCallback(
    (project) => {
      const linkedFindingKeys = project?.linked_findings || [];

      const linkedRisks = linkedFindingKeys
        .map((findingKey) => findingByKey.get(findingKey)?.risk)
        .filter((risk) => risk && risk.risk_id)
        .filter(
          (risk, index, self) =>
            self.findIndex((r) => r.risk_id === risk.risk_id) === index,
        )
        .sort((a, b) => a.risk_id.localeCompare(b.risk_id));

      return linkedRisks;
    },
    [findingByKey],
  );

  const matchesSearch = useCallback(
    (type, item, query) => {
      const q = normalize(query).trim();
      if (!q) return true;

      if (type === "audits") {
        const chapters = _.uniqBy(
          item?.form || [],
          (formItem) =>
            `${formItem?.risk?.qms_id ?? ""} - ${formItem?.risk?.qms_description ?? ""}`,
        )
          .map((formItem) => {
            const id = formItem?.risk?.qms_id ?? "";
            const description = formItem?.risk?.qms_description ?? "";
            return `${id} ${description}`.trim();
          })
          .join(" ");

        return [
          item?.key,
          item?.name,
          item?.responsible,
          item?.status,
          item?.frequency,
          item?.date,
          chapters,
        ].some((value) => normalize(value).includes(q));
      }

      if (type === "findings") {
        return [
          item?.key,
          item?.reference,
          item?.risk?.risk_id,
          item?.risk?.level,
          item?.description,
          item?.responsible,
          item?.status,
          item?.date,
        ].some((value) => normalize(value).includes(q));
      }

      if (type === "improvements") {
        const riskIds = getImprovementRisks(item)
          .map((risk) => risk.risk_id)
          .join(" ");
        const linkedFindingKeys = (item?.linked_findings || []).join(" ");

        return [
          item?.key,
          item?.name,
          item?.description,
          linkedFindingKeys,
          riskIds,
          item?.responsible,
          item?.status,
          item?.date,
        ].some((value) => normalize(value).includes(q));
      }

      return true;
    },
    [getImprovementRisks, normalize],
  );

  const getArchivedColumns = (type, styles) => {
    const selectColumn = {
      accessorKey: "select",
      size: 50,
      cell: ({ row }) => (
        <div className={styles.customCheckboxContainer}>
          {row.getIsSelected() ? (
            <div className={styles.checked} />
          ) : (
            <div className={styles.unchecked} />
          )}
        </div>
      ),
      enableSorting: false,
    };

    if (type === "audits") {
      return [
        selectColumn,
        { accessorKey: "key", header: t("archived.table.key"), size: 90 },
        {
          accessorKey: "name",
          header: t("archived.table.auditName"),
          size: 200,
          enableSorting: false,
        },
        {
          accessorKey: "responsible",
          header: t("archived.table.responsible"),
          size: 180,
          cell: (props) => {
            return props.row.original.responsible ? (
              <div>{props.row.original.responsible}</div>
            ) : (
              <div>{t("archived.table.notDefined")}</div>
            );
          },
          enableSorting: false,
        },
        {
          accessorKey: "status",
          header: t("archived.table.status"),
          cell: (props) => {
            const status = props.getValue();
            let statusClassName = "";

            switch (status) {
              case "Open":
                statusClassName = styles.openStatus;
                break;
              case "In Progress":
                statusClassName = styles.inProgressStatus;
                break;
              case "In Review":
                statusClassName = styles.inReviewStatus;
                break;
              case "Done":
                statusClassName = styles.resolvedStatus;
                break;
              default:
                break;
            }

            return (
              <div className={classNames(styles.statusCell, statusClassName)}>
                {status}
              </div>
            );
          },
          enableSorting: false,
        },
        {
          accessorKey: "date",
          header: t("archived.table.dueDate"),
          cell: (props) => <div>{formatDate(props.getValue())}</div>,
          enableSorting: false,
        },
        {
          accessorKey: "frequency",
          header: t("archived.table.frequency"),
          cell: (props) => (
            <div>
              {props.row.original?.frequency?.charAt(0).toUpperCase() +
                props.row.original?.frequency?.slice(1)}
            </div>
          ),
          enableSorting: false,
        },
        {
          accessorKey: "chapter",
          header: t("archived.table.chapter"),
          cell: (props) => {
            const uniqueRisks = _.uniqBy(
              props.row.original.form,
              (item) => `${item.risk?.qms_id} - ${item.risk?.qms_description}`,
            );
            return (
              <div>
                {uniqueRisks.map((item, index) => (
                  <div key={index}>
                    {item.risk?.qms_id} - {item.risk?.qms_description}
                  </div>
                ))}
              </div>
            );
          },
          size: 280,
          enableSorting: false,
        },
      ];
    }

    if (type === "findings") {
      return [
        selectColumn,
        { accessorKey: "key", header: t("archived.table.key"), size: 90 },
        {
          accessorKey: "reference",
          header: t("archived.table.reference"),
          size: 170,
          enableSorting: false,
        },
        {
          accessorKey: "risk.risk_id",
          header: t("archived.table.risk"),
          size: 100,
          enableSorting: false,
        },
        {
          accessorKey: "risk.level",
          header: t("archived.table.level"),
          size: 100,
          cell: (props) => {
            const level = props.row.original?.risk?.level || "unknown"; // Fallback if level is undefined
            const formattedLevel =
              level.charAt(0).toUpperCase() + level.slice(1).toLowerCase(); // Format to uppercase first letter
            return <div>{formattedLevel}</div>;
          },
          enableSorting: false,
        },
        {
          accessorKey: "description",
          header: t("archived.table.description"),
          size: 300,
          cell: (props) => <div>{truncateText(props.getValue(), 80)}</div>,
          enableSorting: false,
        },
        {
          accessorKey: "responsible",
          header: t("archived.table.responsible"),
          size: 170,
          cell: (props) => {
            return props.row.original.responsible ? (
              <div>{props.row.original.responsible}</div>
            ) : (
              <div>{t("archived.table.notDefined")}</div>
            );
          },
          enableSorting: false,
        },
        {
          accessorKey: "status",
          header: t("archived.table.status"),
          size: 120,
          cell: (props) => {
            const status = props.getValue();
            let statusClassName = "";

            switch (status) {
              case "Open":
                statusClassName = styles.openStatus;
                break;
              case "In Progress":
                statusClassName = styles.inProgressStatus;
                break;
              case "In Review":
                statusClassName = styles.inReviewStatus;
                break;
              case "Done":
                statusClassName = styles.resolvedStatus;
                break;
              default:
                break;
            }

            return (
              <div className={classNames(styles.statusCell, statusClassName)}>
                {status}
              </div>
            );
          },
          enableSorting: false,
        },
        {
          accessorKey: "date",
          header: t("archived.table.date"),
          cell: (props) => <div>{formatDate(props.getValue())}</div>,
          enableSorting: false,
        },
      ];
    }

    if (type === "improvements") {
      return [
        selectColumn,
        {
          accessorKey: "key",
          header: t("archived.table.key"),
          size: 80,
          cell: (props) => <div>{props.getValue()}</div>,
          enableSorting: true,
          sortingFn: "alphanumeric",
        },
        {
          accessorKey: "name",
          header: t("archived.table.name"),
          size: 200,
          cell: (props) => <div>{truncateText(props.getValue(), 60)}</div>,
          enableSorting: false,
        },
        {
          accessorKey: "description",
          header: t("archived.table.description"),
          size: 350,
          cell: (props) => <div>{truncateText(props.getValue(), 80)}</div>,
          enableSorting: false,
        },
        {
          accessorKey: "risks",
          header: t("archived.table.risks"),
          size: 150,
          cell: (props) => {
            const linkedRisks = getImprovementRisks(props.row.original);

            if (linkedRisks.length === 0) {
              return <div>{t("archived.table.notDefined")}</div>;
            }

            return (
              <div className={styles.risksCell}>
                {linkedRisks.map((risk, index) => (
                  <span key={risk.risk_id} className={styles.riskItem}>
                    {risk.risk_id}
                    {index !== linkedRisks.length - 1 && <span>, </span>}
                  </span>
                ))}
              </div>
            );
          },
          enableSorting: false,
        },
        {
          accessorKey: "date",
          header: t("archived.table.date"),
          size: 130,
          cell: (props) => (
            <div>
              {props.getValue() ? formatDate(props.getValue()) : t("archived.table.notDefined")}
            </div>
          ),
          sortingFn: "datetime",
        },
        {
          accessorKey: "responsible",
          header: t("archived.table.responsible"),
          size: 170,
          cell: (props) => {
            return props.row.original.responsible ? (
              <div>{props.row.original.responsible}</div>
            ) : (
              <div>{t("archived.table.notDefined")}</div>
            );
          },
          enableSorting: false,
        },
        {
          accessorKey: "status",
          header: t("archived.table.status"),
          size: 120,
          cell: (props) => {
            const status = props.getValue();
            let statusClassName = "";

            switch (status) {
              case "Open":
                statusClassName = styles.openStatus;
                break;
              case "In Progress":
                statusClassName = styles.inProgressStatus;
                break;
              case "In Review":
                statusClassName = styles.inReviewStatus;
                break;
              case "Done":
                statusClassName = styles.resolvedStatus;
                break;
              default:
                break;
            }

            return (
              <div className={classNames(styles.statusCell, statusClassName)}>
                {status}
              </div>
            );
          },
          enableSorting: false,
        },
      ];
    }

    return [selectColumn];
  };

  const auditColumns = getArchivedColumns("audits", styles);
  const findingColumns = getArchivedColumns("findings", styles);
  const improvementColumns = getArchivedColumns("improvements", styles);

  const filteredAudits = useMemo(
    () =>
      archivedAudits.filter((audit) =>
        matchesSearch("audits", audit, searchQueries.audits),
      ),
    [archivedAudits, matchesSearch, searchQueries.audits],
  );
  const filteredFindings = useMemo(
    () =>
      archivedFindings.filter((finding) =>
        matchesSearch("findings", finding, searchQueries.findings),
      ),
    [archivedFindings, matchesSearch, searchQueries.findings],
  );
  const filteredImprovements = useMemo(
    () =>
      archivedImprovements.filter((improvement) =>
        matchesSearch("improvements", improvement, searchQueries.improvements),
      ),
    [archivedImprovements, matchesSearch, searchQueries.improvements],
  );

  const tabConfig = {
    audits: {
      label: t("archived.sections.audits"),
      count: archivedAudits.length,
      metricIcon: <FileSearch size={18} />,
      accentClass: styles.metricIconNeutral,
      columns: auditColumns,
      data: filteredAudits,
      selected: selectedAudits,
      setSelected: setSelectedAudits,
      search: searchQueries.audits,
      variant: "audits",
    },
    findings: {
      label: t("archived.sections.findings"),
      count: archivedFindings.length,
      metricIcon: <AlertCircle size={18} />,
      accentClass: styles.metricIconWarning,
      columns: findingColumns,
      data: filteredFindings,
      selected: selectedFindings,
      setSelected: setSelectedFindings,
      search: searchQueries.findings,
      variant: "findings",
    },
    improvements: {
      label: t("archived.sections.improvements"),
      count: archivedImprovements.length,
      metricIcon: <TrendingUp size={18} />,
      accentClass: styles.metricIconActive,
      columns: improvementColumns,
      data: filteredImprovements,
      selected: selectedImprovements,
      setSelected: setSelectedImprovements,
      search: searchQueries.improvements,
      variant: "improvementProjects",
    },
  };

  const active = tabConfig[activeTab];
  const selectedCount = active.selected.length;
  const hasSelected = selectedCount > 0;
  const selectedLabel =
    selectedCount === 1
      ? t("archived.selectionBar.single", { count: selectedCount })
      : t("archived.selectionBar.multiple", { count: selectedCount });

  const handleClearSelection = () => {
    resetAllSelectionState();
  };

  const handleTabChange = (nextTab) => {
    setActiveTab(nextTab);
    resetAllSelectionState();
  };

  return (
    <MainLayout>
      <div className={styles.column}>
        <div className={styles.archivedContainer}>
          <div className={styles.pageHeader}>
            <div className={styles.titleRow}>
              <div className={styles.titleIcon}>
                <Archive size={32} />
              </div>
              <div className={styles.pageTitle}>{t("archived.title")}</div>
            </div>
            <div className={styles.pageSubtitle}>{t("archived.subtitle")}</div>
          </div>

          <div className={styles.metricGrid}>
            {["audits", "findings", "improvements"].map((type) => {
              const tab = tabConfig[type];
              const isActive = type === activeTab;

              return (
                <button
                  key={type}
                  type="button"
                  className={classNames(
                    styles.metricCard,
                    isActive && styles.metricCardActive,
                  )}
                  onClick={() => handleTabChange(type)}
                >
                  <div className={styles.metricHeader}>
                    <span className={styles.metricTitle}>{tab.label}</span>
                    <span
                      className={classNames(
                        styles.metricIconBadge,
                        tab.accentClass,
                      )}
                    >
                      {tab.metricIcon}
                    </span>
                  </div>
                  <div className={styles.metricValue}>{tab.count}</div>
                </button>
              );
            })}
          </div>

          {totalArchived === 0 ? (
            <div className={styles.emptyState}>
              <img
                src={ArchiveIllustration}
                alt=""
                className={styles.emptyIllustration}
              />
              <div className={styles.emptyCopy}>{t("archived.noData")}</div>
            </div>
          ) : (
            <>
              <div className={styles.tabsRow}>
                <div className={styles.tabSwitcher}>
                  {["audits", "findings", "improvements"].map((type) => {
                    const tab = tabConfig[type];
                    const isActive = type === activeTab;

                    return (
                      <button
                        key={type}
                        type="button"
                        className={classNames(
                          styles.tabButton,
                          isActive && styles.tabButtonActive,
                        )}
                        onClick={() => handleTabChange(type)}
                      >
                        {tab.label}
                        <span className={styles.tabCount}>{tab.count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className={styles.actionsRow}>
                <Input
                  type="text"
                  placeholder={t("archived.searchPlaceholder")}
                  value={active.search}
                  onChange={(e) =>
                    setSearchQueries((prev) => ({
                      ...prev,
                      [activeTab]: e.target.value,
                    }))
                  }
                  className={styles.input}
                  wrapperClassName={styles.searchWrapper}
                  icon={SearchIcon}
                  isClearable
                  onClear={() =>
                    setSearchQueries((prev) => ({
                      ...prev,
                      [activeTab]: "",
                    }))
                  }
                />
              </div>

              {hasSelected && (
                <div className={styles.selectionBar}>
                  <div className={styles.selectionMeta}>
                    <span className={styles.selectionCount}>{selectedLabel}</span>
                    <button
                      type="button"
                      className={styles.selectionClearButton}
                      onClick={handleClearSelection}
                    >
                      {t("archived.selectionBar.clear")}
                    </button>
                  </div>
                  <Button
                    text={t("archived.unarchive")}
                    onClick={() => handleUnarchive(active.selected, activeTab)}
                    disabled={!active.selected.length}
                    className={styles.primaryAction}
                    icon={<RotateCcw size={16} />}
                    iconPosition="left"
                    type="button"
                  />
                </div>
              )}

              <div
                className={classNames(
                  styles.tableCard,
                  !active.data.length && styles.tableCardEmpty,
                )}
              >
                <Table
                  columns={active.columns}
                  data={active.data}
                  onSelectedRowsChange={active.setSelected}
                  resetSelection={resetRowSelection}
                  centered
                  variant={active.variant}
                />
                {!active.data.length && (
                  <div className={styles.tableEmptyState}>
                    <div className={styles.tableEmptyContent}>
                      <div className={styles.tableEmptyCopy}>
                        {t("archived.noData")}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Archived;
